/**
 * e-Invoice Past-Window Interstitial (design "Modals") — two paths: guided fix
 * (recommended) vs export-anyway (gated behind an acknowledgment checkbox).
 */
import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import ModalShell from './modalShell';
import { useTheme } from '../../context/themeContext';
import { useResponsive } from '../../utils/responsive';
import SkIcon from '../skIcon';

interface Props {
  visible: boolean;
  onClose: () => void;
  number: string;
  issuedDate: string;
  /** Open a credit note against this invoice — the corrective path. */
  onGuidedFix: () => void;
  /** Export past the window with the override recorded on the server. */
  onExportAnyway: () => Promise<void>;
}

const EInvoiceModal: React.FC<Props> = ({ visible, onClose, number, issuedDate, onGuidedFix, onExportAnyway }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const { isMobile } = useResponsive();
  const [ack, setAck] = useState(false);

  const fix = () => { onClose(); onGuidedFix(); };
  const exportAnyway = async () => {
    if (!ack) return;
    try {
      await onExportAnyway();
      onClose();
    } catch {
      // the service layer showed the server's reason; keep the dialog open
    }
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="This invoice is outside the reporting window"
      subtitle={`${number} · issued ${issuedDate} · the IRP will reject it`}
      headerTone="danger"
      width={620}
      footer={
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, flex: 1 }}>Window &amp; threshold are configured values · regulation may change</Text>
          <Pressable onPress={onClose} style={{ height: 32, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink2 }}>Cancel</Text>
          </Pressable>
        </View>
      }
    >
      <View style={{ gap: 16 }}>
        <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink2, lineHeight: 20 }}>
          Your region pack sets a <Text style={{ fontWeight: '700' }}>30-day e-invoice reporting window</Text>. This invoice is now <Text style={{ fontWeight: '700' }}>9 days past</Text> that limit, so the Invoice Registration Portal will return an error if you submit it. Choose how to proceed:
        </Text>
        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
          {/* Fix it properly */}
          <View style={{ flex: 1, borderWidth: 1.5, borderColor: colors.accentLine, borderRadius: 8, padding: 15, backgroundColor: colors.accentSoft }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 7 }}>
              <View style={{ width: 26, height: 26, borderRadius: 7, backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center' }}>
                <SkIcon name="checkCircle" size={14} color={colors.accent} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.accentInk }}>Fix it properly</Text>
              <View style={{ paddingHorizontal: 6, paddingVertical: 1, borderRadius: 999, backgroundColor: colors.accent }}>
                <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff' }}>RECOMMENDED</Text>
              </View>
            </View>
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2, lineHeight: 17, marginBottom: 12 }}>
              Guided flow: raise a pre-filled <Text style={{ fontWeight: '700' }}>credit note</Text> against this invoice, then issue a <Text style={{ fontWeight: '700' }}>fresh invoice</Text> dated today — fully compliant and inside the window.
            </Text>
            <Pressable onPress={fix} style={{ height: 34, borderRadius: 5, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: '#fff' }}>Start guided fix</Text>
            </Pressable>
          </View>
          {/* Export anyway */}
          <View style={{ flex: 1, borderWidth: 1, borderColor: colors.line, borderRadius: 8, padding: 15, backgroundColor: colors.card }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 7 }}>
              <View style={{ width: 26, height: 26, borderRadius: 7, backgroundColor: colors.paper2, alignItems: 'center', justifyContent: 'center' }}>
                <SkIcon name="download" size={14} color={colors.ink3} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.ink2 }}>Export anyway</Text>
            </View>
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3, lineHeight: 17, marginBottom: 12 }}>
              Generate the JSON and submit despite the warning. The override is <Text style={{ fontWeight: '700' }}>recorded against your name</Text> for audit. Expect an IRP rejection.
            </Text>
            <Pressable onPress={() => setAck((a) => !a)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <View style={{ width: 14, height: 14, borderRadius: 3, borderWidth: 1.5, borderColor: ack ? colors.danger : colors.lineStrong, backgroundColor: ack ? colors.danger : colors.card, alignItems: 'center', justifyContent: 'center' }}>
                {ack && <SkIcon name="check" size={9} color="#fff" strokeWidth={3} />}
              </View>
              <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2 }}>I understand and accept the risk</Text>
            </Pressable>
            <Pressable onPress={exportAnyway} style={{ height: 34, borderRadius: 5, alignItems: 'center', justifyContent: 'center', backgroundColor: ack ? colors.danger : colors.paper3, borderWidth: 1, borderColor: ack ? colors.danger : colors.line }}>
              <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: ack ? '#fff' : colors.ink4 }}>Export anyway</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </ModalShell>
  );
};

export default EInvoiceModal;
